const TYPES = ['direct', 'saas', 'integrator'];
// Doit matcher `value` dans src/utils/select.js → country (sinon getLabel crash via ramda pick)
const COUNTRIES = ['england', 'france', 'germany', 'tunisia'];
const INDUSTRIES = [
  'Banking', 'Insurance', 'Retail', 'Healthcare', 'Logistics',
  'Energy', 'Telecom', 'Public Sector', 'Manufacturing', 'Media',
];

const COMPANY_NAMES = [
  'Atlas Banking Group',
  'NovaCare Insurance',
  'Helix Retail SAS',
  'Beacon Logistics International',
  'Polaris Energy Holdings',
  'TerraTel Networks',
  'Meridien Public Services',
  'CrescentSteel Manufacturing',
  'Lumière Media Group',
  'Quantum Health Systems',
  'BrightWave Pharmaceuticals',
  'OrionPay Financial',
  'Saphir Voyages',
  'Verdant Agritech',
  'Stellar Defense Solutions',
  'Forge & Co. Industries',
  'Pelagos Maritime',
  'Coraline Real Estate',
  'Cobalt Mining Corp',
  'Sienna Hospitality',
  'Northstar Aviation',
  'Pixel Forge Studios',
  'Vector Robotics',
  'Aurum Asset Management',
  'KapitalOne Services',
];

const rng = (seed) => {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
};

const pick = (arr, r) => arr[Math.floor(r() * arr.length)];

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};

const buildClient = (i, r) => {
  const type = pick(TYPES, r);
  const country = pick(COUNTRIES, r);
  const industry = pick(INDUSTRIES, r);
  const status = r() < 0.78 ? 'OPEN' : 'CLOSED';
  const name = COMPANY_NAMES[i % COMPANY_NAMES.length];
  const slug = name.toLowerCase().replace(/[^a-z]/g, '');
  const createdAt = daysAgo(Math.floor(r() * 700) + 5);
  return {
    id: `cli-${String(i + 1).padStart(4, '0')}`,
    code: `CLI-${String(i + 1).padStart(4, '0')}`,
    name,
    industry,
    type,
    country,
    language: country === 'france' ? 'fr' : 'en',
    status,
    address: `${10 + Math.floor(r() * 200)} Boulevard Haussmann`,
    city: ['Paris', 'London', 'Berlin', 'Tunis'][Math.floor(r() * 4)],
    zipCode: String(10000 + Math.floor(r() * 89999)),
    phone: `+33 ${Math.floor(r() * 9)} ${String(10 + Math.floor(r() * 89))} ${String(10 + Math.floor(r() * 89))} ${String(10 + Math.floor(r() * 89))} ${String(10 + Math.floor(r() * 89))}`,
    email: `contact@${slug}.com`,
    nbrOpenAccount: Math.floor(r() * 280) + 12,
    nbrOpenAlert: Math.floor(r() * 18),
    createDate: createdAt,
    // Le composant EditClient utilise contratDate (typo "contrat") pour la "Open Date"
    contratDate: createdAt,
    // Note HTML, lue via dangerouslySetInnerHTML quand non en edit mode
    note: `<p>Key Account Manager: Sven Almeida</p><p>SLA tier: <b>Gold</b></p><p>2FA mandatory on LEVEL_3 conventions.</p>`,
    mainContactFirstName: ['Camille', 'Hugo', 'Léa', 'Sven', 'Amara', 'Yusra', 'Otto', 'Mei'][Math.floor(r() * 8)],
    mainContactLastName: ['Durand', 'Schmidt', 'Almeida', 'O\'Connor', 'Ben Salem', 'Nakamura', 'Petrov', 'Larsen'][Math.floor(r() * 8)],
    mainContactEmail: `contact${i}@example.com`,
    mainContactPhone: `+44 20 ${String(1000 + Math.floor(r() * 8999))} ${String(1000 + Math.floor(r() * 8999))}`,
  };
};

const r = rng(7);
export const ALL_CLIENTS = Array.from({ length: COMPANY_NAMES.length }, (_, i) => buildClient(i, r));

export const findClient = (id) => ALL_CLIENTS.find((c) => c.id === id);

export const filterClients = ({ query = '', status = '', type = '' }) => {
  const q = query.trim().toLowerCase();
  return ALL_CLIENTS.filter((c) => {
    if (q && !c.name.toLowerCase().includes(q) && !c.industry.toLowerCase().includes(q)) return false;
    if (status && c.status !== status) return false;
    if (type && c.type !== type) return false;
    return true;
  });
};

export const computeTypeStats = (clients) => {
  const typeMap = {};
  const statusMap = {};
  clients.forEach((c) => {
    typeMap[c.type] = (typeMap[c.type] || 0) + 1;
    statusMap[c.status] = (statusMap[c.status] || 0) + 1;
  });
  return {
    type: Object.entries(typeMap).map(([type, count]) => ({ type, count })),
    status: Object.entries(statusMap).map(([status, count]) => ({ status, count })),
  };
};

const USER_TYPES = ['employee', 'temp', 'client', 'supplier'];
const LEVELS = ['LEVEL_1', 'LEVEL_2', 'LEVEL_3'];
const ACCOUNT_STATUS = ['OPEN', 'CLOSED'];
const CONVENTION_STATUS = ['ACTIVE', 'PENDING', 'BLOCKED', 'SUSPENDED', 'EXPIRED', 'SATURATED'];

const FIRST_NAMES = ['Alice', 'Boris', 'Camille', 'David', 'Élodie', 'Farouk', 'Gretel', 'Hadi', 'Iris', 'Jules', 'Kenza', 'Liam', 'Maya', 'Noé', 'Olga', 'Paulo', 'Quentin', 'Rania', 'Samir', 'Tessa', 'Ulysse', 'Vera', 'Wassim', 'Ximena', 'Yann', 'Zoé'];
const LAST_NAMES = ['Martin', 'Bernard', 'Petit', 'Dubois', 'Moreau', 'Laurent', 'Simon', 'Michel', 'Lefèvre', 'Leroy', 'Roux', 'David', 'Bertrand', 'Morel', 'Fournier', 'Girard', 'Bonnet', 'Dupont', 'Lambert', 'Fontaine'];

export const buildAccountsForClient = (clientId) => {
  const seed = parseInt(clientId.split('-')[1] || '1', 10);
  const r2 = rng(seed * 31);
  const count = 25 + Math.floor(r2() * 35);
  const ALERTS = ['', '', '', '', 'Failed login spike', 'Inactive 90d+', 'Convention near expiry'];
  return Array.from({ length: count }, (_, i) => {
    const firstName = pick(FIRST_NAMES, r2);
    const lastName = pick(LAST_NAMES, r2);
    const hiaId = `${firstName.charAt(0).toLowerCase()}${lastName.toLowerCase()}${i}`;
    return {
      id: `${clientId}-acc-${String(i + 1).padStart(3, '0')}`,
      hiaId,
      firstName,
      lastName,
      userId: hiaId,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
      type: pick(USER_TYPES, r2),
      // UsersTable lit `securityLevel`, l'API filtre via `level` → on garde une seule valeur cohérente
      ...(() => { const lvl = pick(LEVELS, r2); return { securityLevel: lvl, level: lvl }; })(),
      accountStatus: pick(ACCOUNT_STATUS, r2),
      conventionStatus: pick(CONVENTION_STATUS, r2),
      // UsersTable lit `conventionStartDate` pour "Last activity"
      conventionStartDate: daysAgo(Math.floor(r2() * 500) + 1),
      alert: pick(ALERTS, r2),
      lastLogin: daysAgo(Math.floor(r2() * 60)),
      createDate: daysAgo(Math.floor(r2() * 500) + 30),
    };
  });
};

export const buildConventionsForClient = (clientId) => {
  const seed = parseInt(clientId.split('-')[1] || '1', 10);
  const r3 = rng(seed * 53);
  const families = [
    'Default conventions', 'Privileged users', 'External contractors',
    'API service accounts', 'Audit reviewers', 'Vendor portals',
    'Backoffice operators', 'Customer support', 'Read-only analysts',
    'Emergency break-glass',
  ];
  return families.map((name, i) => {
    const lvl = pick(LEVELS, r3);
    return {
      id: `${clientId}-fam-${i + 1}`,
      name,
      // ConventionSettings lit `level` (avec getLabel), API renvoie souvent `securityLevel` aussi → on fournit les deux
      level: lvl,
      securityLevel: lvl,
      active: r3() > 0.25,
      // ConventionSettings lit `createdDate` (formatDate)
      createdDate: daysAgo(Math.floor(r3() * 800) + 30),
      nbrAccount: Math.floor(r3() * 80) + 1,
      description: `${name} — security family for ${clientId}`,
    };
  });
};

export const buildNotesForClient = (clientId) => ({
  note: `Internal note for ${clientId}\n\n- Last review: 2026-03-14\n- KAM: Sven Almeida\n- SLA tier: Gold\n- Special handling: 2FA mandatory on LEVEL_3 conventions`,
});

export const buildOpenAccountsSeries = (clientId) => {
  const seed = clientId ? parseInt(clientId.split('-')[1] || '1', 10) : 1;
  const r4 = rng(seed * 71);
  return Array.from({ length: 30 }, (_, i) => ({
    date: daysAgo(29 - i),
    count: 80 + Math.floor(r4() * 40),
  }));
};

const buildKpiBranch = (seriesNames, seed) => {
  const r = rng(seed);
  const out = [];
  for (let day = 29; day >= 0; day--) {
    const dateStr = daysAgo(day);
    seriesNames.forEach((s) => {
      const count = 5 + Math.floor(r() * 30);
      out.push({ date: dateStr, serie: s, count, total: count + Math.floor(r() * 50) });
    });
  }
  return out;
};

// Le composant Charts lit kpiStatistics.all / .type / .level — toujours retourner les 3 branches.
export const buildKpiSeries = (clientId) => {
  const seed = (clientId ? parseInt(clientId.split('-')[1] || '1', 10) : 1) * 89;
  return {
    all: buildKpiBranch(['all'], seed),
    type: buildKpiBranch(USER_TYPES, seed + 17),
    level: buildKpiBranch(LEVELS, seed + 31),
  };
};

export const buildClientUsersCount = (clientId) => {
  const seed = parseInt(clientId.split('-')[1] || '1', 10);
  const r6 = rng(seed * 13);
  return {
    typeStats: USER_TYPES.map((type) => ({ type, count: Math.floor(r6() * 60) + 5 })),
    levelStats: LEVELS.map((level) => ({ level, count: Math.floor(r6() * 60) + 5 })),
    accountStatusStats: ACCOUNT_STATUS.map((accountStatus) => ({ accountStatus, count: Math.floor(r6() * 80) + 5 })),
    conventionStatusStats: CONVENTION_STATUS.map((conventionStatus) => ({ conventionStatus, count: Math.floor(r6() * 30) + 1 })),
  };
};
